import { useEffect } from "react";
import { useAuthState } from "@/hooks/useAuthState";
import { supabase } from "@/integrations/supabase/client";

/**
 * Component that automatically applies user theme from user_settings on login
 * This component has one single responsibility: read theme from DB → apply to DOM
 */
export const UserThemeApplier = () => {
  const { session } = useAuthState();

  useEffect(() => {
    const applyUserTheme = async () => {
      if (!session?.user?.id) {
        // User logged out - apply default light theme
        document.documentElement.className = "light";
        return;
      }

      // First, try to get theme from localStorage (synchronous)
      const storedThemeKey = `theme_${session.user.id}`;
      const storedTheme = localStorage.getItem(storedThemeKey);
      const validThemes = ["light", "dark", "ocean", "forest", "sunset"];
      
      if (storedTheme && validThemes.includes(storedTheme)) {
        // Apply stored theme immediately to prevent FOUC
        console.log("Applying theme from localStorage:", storedTheme);
        document.documentElement.className = storedTheme;
      }

      try {
        // Then fetch from database for consistency
        const { data, error } = await supabase
          .from("user_settings")
          .select("theme")
          .eq("user_id", session.user.id)
          .single();

        if (error) {
          console.error("Error fetching user theme:", error);
          // If no localStorage theme, fallback to light
          if (!storedTheme) {
            document.documentElement.className = "light";
          }
          return;
        }

        const dbTheme = data?.theme || "light";
        
        // Only update if DB theme is different from localStorage theme
        if (dbTheme !== storedTheme) {
          console.log("Updating theme from database:", dbTheme);
          if (validThemes.includes(dbTheme)) {
            document.documentElement.className = dbTheme;
            // Update localStorage to stay in sync
            localStorage.setItem(storedThemeKey, dbTheme);
          }
        }

      } catch (error) {
        console.error("Unexpected error applying user theme:", error);
        // If no localStorage theme, fallback to light
        if (!storedTheme) {
          document.documentElement.className = "light";
        }
      }
    };

    applyUserTheme();
  }, [session]);

  return null;
};